import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTopButton from '@/components/feature/ScrollToTopButton';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo, reputationItems } from '@/mocks/articles';

function BrandIntro() {
  return (
    <section className="py-16 md:py-20 px-4 md:px-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col lg:flex-row items-start gap-12">
          {/* Text */}
          <div className="flex-1">
            <span className="text-xs font-medium text-primary-600 bg-primary-100 px-3 py-1 rounded-full">
              品牌介绍
            </span>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mt-3 mb-6">
              关于悠之空
            </h2>
            <div className="space-y-4 text-foreground-600 leading-relaxed text-base">
              <p>
                悠之空是一个专注于视觉小说《缘之空》的深度内容平台。我们相信，<strong>缘之空不仅仅是一部视觉小说，更是一部值得被认真对待的文学作品</strong>。它以细腻的笔触描绘了禁忌情感与命运羁绊，探讨了爱、孤独、自由与存在的永恒主题。
              </p>
              <p>
                我们的团队由一群热爱ACG文化的创作者组成，致力于为缘之空爱好者提供最全面、最深度的内容服务——从剧情攻略、角色解析到主题评测，从玩家社区到文化讨论，我们希望搭建一个能让同好交流、思考、感动的平台。
              </p>
              <p>
                自{siteInfo.foundingYear}年创立以来，悠之空已累计发表超过200篇原创深度文章，服务了超过5万名忠实读者。我们始终坚持以<strong>专业、深度、真诚</strong>的态度对待每一篇文章、每一个观点。
              </p>
            </div>
          </div>

          {/* Image */}
          <div className="lg:w-80 flex-shrink-0 w-full">
            <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Cozy%20Japanese%20style%20reading%20room%20with%20wooden%20bookshelf%20filled%20with%20visual%20novel%20and%20manga%2C%20warm%20soft%20lighting%20from%20paper%20lantern%2C%20cherry%20blossom%20in%20vase%2C%20peaceful%20quiet%20atmosphere%2C%20literary%20aesthetic%2C%20editorial%20photography&width=800&height=1000&seq=yosuga-about-01&orientation=portrait"
                alt="悠之空编辑部"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Reputation() {
  return (
    <section className="py-16 md:py-20 bg-background-100">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <span className="text-xs font-medium text-accent-600 bg-accent-100 px-3 py-1 rounded-full">
            口碑与数据
          </span>
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mt-3">
            我们的影响力
          </h2>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
          {reputationItems.map((item) => (
            <div key={item.label} className="bg-background-50 border border-background-200 rounded-lg p-6 text-center">
              <div className="text-3xl md:text-4xl font-heading font-bold text-primary-600 mb-2">
                {item.value}
              </div>
              <div className="text-sm text-foreground-500">{item.label}</div>
            </div>
          ))}
        </div>

        {/* Testimonials */}
        <h3 className="font-heading font-semibold text-xl text-foreground-950 text-center mb-8">
          读者评价
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              quote: '悠之空的文章让我重新认识了缘之空。原本只是被画面和音乐吸引，读了角色解析后才真正理解了故事背后的深意。',
              author: '视觉小说爱好者 - 小林',
            },
            {
              quote: '作为一个玩了五遍缘之空的老玩家，我以为自己已经足够了解这部作品。但悠之空的主题评测让我看到了全新的解读角度，非常惊喜。',
              author: '资深玩家 - 阿明',
            },
            {
              quote: '剧情攻略写得非常详细，帮助我顺利解锁了所有结局。感谢悠之空团队的用心付出！',
              author: '新手玩家 - 小月',
            },
          ].map((item) => (
            <div key={item.author} className="bg-background-50 border border-background-200 rounded-lg p-6">
              <div className="flex items-center gap-1 mb-4 text-accent-500">
                {[1, 2, 3, 4, 5].map((s) => (
                  <i key={s} className="ri-star-fill text-xs"></i>
                ))}
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4 italic">
                "{item.quote}"
              </p>
              <div className="text-xs text-foreground-500 font-medium">—— {item.author}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactForm() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9u1ttv8l97n3eas3fq0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([k]) => k !== 'phone_alt')
            .map(([k, v]) => [k, String(v)]),
        ).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(text);
      } catch {
        // ignore
      }
      if (res.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg =
          (parsed as Record<string, unknown> | null)?.meta &&
          ((parsed as Record<string, unknown>).meta as Record<string, string>).message
            ? ((parsed as Record<string, unknown>).meta as Record<string, string>).message
            : '提交失败，请稍后再试';
        setFormError(String(msg || '提交失败，请稍后再试'));
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <section id="contact" className="py-16 md:py-20 px-4 md:px-6">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <span className="text-xs font-medium text-secondary-600 bg-secondary-100 px-3 py-1 rounded-full">
            联系我们
          </span>
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mt-3">
            期待与您交流
          </h2>
          <p className="text-sm text-foreground-500 mt-2">
            无论是对缘之空的讨论、投稿合作，还是网站建议，都欢迎通过以下表单与我们联系
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-readdy-form=""
          className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8"
        >
          {formStatus === 'success' && (
            <div className="mb-6 bg-primary-50 border border-primary-200 rounded-lg px-4 py-3 flex items-center gap-3">
              <i className="ri-checkbox-circle-line text-primary-600 text-lg"></i>
              <span className="text-sm text-primary-700">感谢您的留言！我们会尽快回复您的消息。</span>
            </div>
          )}
          {formStatus === 'error' && (
            <div className="mb-6 bg-accent-50 border border-accent-200 rounded-lg px-4 py-3 flex items-center gap-3">
              <i className="ri-error-warning-line text-accent-600 text-lg"></i>
              <span className="text-sm text-accent-700">{formError}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                您的姓名 <span className="text-accent-500">*</span>
              </label>
              <input
                id="contact-name"
                name="name"
                type="text"
                required
                placeholder="请输入您的姓名"
                className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                电子邮箱 <span className="text-accent-500">*</span>
              </label>
              <input
                id="contact-email"
                name="email"
                type="email"
                required
                placeholder="请输入您的邮箱"
                className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
              主题 <span className="text-accent-500">*</span>
            </label>
            <input
              id="contact-subject"
              name="subject"
              type="text"
              required
              placeholder="请输入留言主题"
              className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
              留言内容 <span className="text-accent-500">*</span>
            </label>
            <textarea
              id="contact-message"
              name="message"
              required
              rows={5}
              maxLength={500}
              placeholder="请输入您的留言内容（最多500字）"
              className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
            ></textarea>
            <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
          </div>

          {/* Honeypot */}
          <div className="website-alt-wrapper" aria-hidden="true">
            <label htmlFor="phone_alt">phone_alt</label>
            <input
              id="phone_alt"
              name="phone_alt"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />
          </div>

          <button
            type="submit"
            disabled={formStatus === 'loading'}
            className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 font-medium px-6 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer text-sm"
          >
            {formStatus === 'loading' ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                提交中...
              </>
            ) : (
              <>
                <i className="ri-send-plane-line"></i>
                发送留言
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}

export default function About() {
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: 'https://knotheart.github.io',
    description: siteInfo.description,
    foundingDate: siteInfo.foundingYear,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <Navbar />
      <ScrollToTop />
      <main>
        <div className="bg-foreground-950 pt-24 pb-10 px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-background-50 mt-4">
              关于悠之空
            </h1>
            <p className="text-foreground-400 mt-2 text-base">
              了解我们的品牌故事、团队愿景，以及为缘之空爱好者创造的价值
            </p>
          </div>
        </div>
        <BrandIntro />
        <Reputation />
        <ContactForm />
      </main>
      <Footer />
      <ScrollToTopButton />
    </>
  );
}